/**
 * DraftTab — the Inspector's primary surface: one section's drafted text
 * rendered as Markdown, and nothing else (no per-sentence citation reveals or
 * citation strips — the user wants the section output only).
 *
 * Data source: getSectionContent(runId, moduleId) — returns the draft sections.
 *
 * Component stability: the markdown component overrides are memoized once per
 * DraftTab instance so react-markdown never remounts the subtree.
 */

import { makeStyles } from "@fluentui/react-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { getSectionContent } from "../../../api/client";
import { remarkSubSup } from "../../../services/remarkSubSup";
import { remarkSoftBreaks } from "../../../services/remarkSoftBreaks";
import { decodeEscapedWhitespace, normalizeTableSpacing } from "../../../services/markdownNormalize";
import { repairPartialTables } from "../../../services/partialTables";
import type { SpineSection } from "../../hooks/useVariantCRun";
import {
  CITATION_BRACKET_PATTERN,
  classifyCitationBracket,
  matchFlagMarker,
  sanitizeDraftForWord,
} from "../../services/draftSanitizer";
import { hasTitleFields } from "../../services/titleFieldsView";
import { Icon, Notice } from "../../../ds/components";
import type { AgentActivityLine } from "../../services/agentActivityFeed";
import { fonts } from "../../tokens";
import TitleFieldsPreview from "./TitleFieldsPreview";

const NO_ACTIVITY: readonly AgentActivityLine[] = [];

interface DraftTabProps {
  section: SpineSection;
  runId: string;
  /** The section's live agent activity, shown as a checklist while it drafts. */
  activity?: readonly AgentActivityLine[];
}

interface SectionPayload {
  sections: Array<{
    section_number: string;
    section_title: string;
    draft_content: string;
  }>;
  /** Structured title-page fields (label→value). A title-page module has no
   *  prose body — its content lives here. Attached at the top level of the
   *  section response by the backend; rendered via TitleFieldsPreview. */
  title_fields?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Block-structure helpers — lists, soft breaks, bold-only "headers"
// ---------------------------------------------------------------------------

/**
 * React context flag: true while rendering inside a <ul>/<ol>. The paragraph
 * renderer reads it to skip the bold-line→<h4> promotion that only makes sense
 * for top-level prose — inside a list item that machinery shatters the list.
 */
const InListContext = React.createContext(false);

/**
 * A paragraph whose only meaningful child is a single **bold** run is the
 * model's idiom for a sub-heading (e.g. a standalone `**Overview**` line). We
 * promote ONLY that exact shape to <h4>. We inspect the hast node (not the
 * already-rendered React children, whose <strong> is our own component) so the
 * check stays reliable. The old length/punctuation heuristic mis-promoted
 * ordinary short sentences and list items, so real headings looked wrong.
 */
function isBoldOnlyParagraph(node: unknown): boolean {
  const children = (node as { children?: Array<{ type?: string; tagName?: string; value?: string }> })?.children;
  if (!Array.isArray(children)) return false;
  const meaningful = children.filter((c) => !(c.type === "text" && /^\s*$/.test(c.value ?? "")));
  return meaningful.length === 1 && meaningful[0].type === "element" && meaningful[0].tagName === "strong";
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const SERIF = "var(--font-serif)";
const INK = "var(--ink)";

const useStyles = makeStyles({
  root: {
    padding: "16px 16px 24px",
    fontFamily: fonts.body,
    color: INK,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "20px 0",
    color: "var(--ink-muted)",
    fontSize: "13px",
  },
  // Same calm empty state as the other inspector tabs.
  emptyState: {
    padding: "8px 0",
    color: "var(--ink-muted)",
    fontSize: "13px",
    lineHeight: "20px",
  },
  paragraph: {
    margin: "0 0 12px",
    font: `400 14px/22px ${SERIF}`,
    color: INK,
  },
  sectionMeta: {
    font: "500 11px/16px var(--font-sans)",
    color: "var(--ink-muted)",
    letterSpacing: ".06em",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  h2: {
    font: "600 15px/22px var(--font-sans)",
    color: INK,
    margin: "16px 0 8px",
  },
  h3: {
    font: "600 14px/20px var(--font-sans)",
    color: INK,
    margin: "14px 0 6px",
  },
  h4: {
    font: "600 13px/20px var(--font-sans)",
    color: INK,
    margin: "12px 0 6px",
  },
  ul: {
    display: "block",
    margin: "0 0 12px",
    paddingLeft: "22px",
    font: `400 14px/22px ${SERIF}`,
    color: INK,
    listStyleType: "disc",
    listStylePosition: "outside",
  },
  ol: {
    display: "block",
    margin: "0 0 12px",
    paddingLeft: "22px",
    font: `400 14px/22px ${SERIF}`,
    color: INK,
    listStyleType: "decimal",
    listStylePosition: "outside",
  },
  li: {
    display: "list-item",
    marginBottom: "4px",
  },
  // Paragraph rendered inside a list item: plain prose and no block decoration, so the list marker and spacing stay clean.
  listParagraph: {
    margin: 0,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    margin: "0 0 12px",
    font: "400 12px/16px var(--font-sans)",
    border: "1px solid var(--line)",
    // repairPartialTables emits a blank header row for a table cut above its header.
    "& thead:not(:has(th:not(:empty)))": { display: "none" },
  },
  th: {
    backgroundColor: "var(--surface-sunken)",
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 600,
    color: INK,
    // Full cell border so the grid shows both horizontal AND vertical rules.
    border: "1px solid var(--line)",
    verticalAlign: "top",
  },
  td: {
    padding: "6px 8px",
    border: "1px solid var(--line)",
    color: INK,
    verticalAlign: "top",
  },
  strong: {
    fontWeight: 600,
    color: INK,
  },
  em: {
    fontStyle: "italic",
    color: INK,
  },
  // ~sub~ / ^sup^ markers (remarkSubSup). line-height 0 stops the raised /
  // lowered glyph from stretching the line box (jitter between lines).
  subSup: {
    fontSize: "0.75em",
    lineHeight: 0,
    color: INK,
  },
  code: {
    backgroundColor: "var(--surface-sunken)",
    padding: "2px 4px",
    borderRadius: "2px",
    fontFamily: fonts.mono,
    fontSize: "12px",
    color: INK,
  },
  gapNotice: {
    marginTop: "4px",
  },
  // Running state (design board 12)
  writingTitle: {
    margin: "0 0 4px",
    font: "600 14px/20px var(--font-sans)",
    color: INK,
  },
  writingText: {
    margin: "0 0 12px",
    font: "400 13px/20px var(--font-sans)",
    color: "var(--ink-muted)",
  },
  skeleton: {
    display: "grid",
    gap: "10px",
    marginTop: "20px",
  },
  skeletonBar: {
    height: "10px",
    borderRadius: "5px",
    backgroundColor: "var(--surface-sunken)",
  },
});

// ---------------------------------------------------------------------------
// Inline markers (design board 13): citations read as chips, gaps as a
// highlighted span. The text itself is untouched; only its presentation.
// ---------------------------------------------------------------------------

const GAP_PREFIXES = ["[MISSING INFO:", "[MISSING TABLE", "[NOT FOUND"];

/** Number of content gaps the draft text still carries. Pure. */
export function countDraftGaps(text: string): number {
  let n = 0;
  for (const m of text.matchAll(CITATION_BRACKET_PATTERN)) {
    if (GAP_PREFIXES.some((p) => m[0].startsWith(p))) n += 1;
  }
  return n;
}

function decorateText(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(CITATION_BRACKET_PATTERN)) {
    const at = m.index ?? 0;
    const whole = m[0];
    const isFlag = matchFlagMarker(whole) !== null;
    const isCite = !isFlag && classifyCitationBracket(m[1]) !== null;
    if (!isFlag && !isCite) continue;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      isFlag ? (
        <span key={`${keyBase}-${i++}`} className="as-gap">
          {whole}
        </span>
      ) : (
        <span key={`${keyBase}-${i++}`} className="as-cite">
          {m[1]}
        </span>
      ),
    );
    last = at + whole.length;
  }
  if (out.length === 0) return [text];
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function decorate(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child, idx) =>
    typeof child === "string" ? decorateText(child, `m${idx}`) : child,
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DraftTab: React.FC<DraftTabProps> = ({ section, runId, activity = NO_ACTIVITY }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<SectionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestKeyRef = useRef<string>("");

  // A failed section produced no body — don't fetch (the content route 404s).
  const draftFailed = !!section.failed && section.status === "flagged";
  const hasDraftContent =
    !draftFailed &&
    (section.status === "drafted" || section.status === "needs_approval" || section.status === "flagged");

  useEffect(() => {
    if (!hasDraftContent) {
      setLoading(false);
      setError(null);
      setPayload(null);
      return;
    }
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setPayload(null);
    (async () => {
      try {
        const resp = await getSectionContent(runId, section.id);
        if (cancelled || requestKeyRef.current !== key) return;
        setPayload(resp as unknown as SectionPayload);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, hasDraftContent]);

  // Memoize the components object so react-markdown never remounts
  const markdownComponents: Components = useMemo(() => {
    return {
      p: ({ node, children, ...props }) => {
        const insideList = React.useContext(InListContext);

        // Inside a list item: plain prose, no bold→<h4> promotion (it would
        // shatter the list marker and spacing).
        if (insideList) {
          return <div className={styles.listParagraph}>{decorate(children)}</div>;
        }

        // A standalone **bold** line is the model's sub-heading idiom → <h4>.
        if (isBoldOnlyParagraph(node)) {
          return (
            <h4 className={styles.h4} {...props}>
              {children}
            </h4>
          );
        }

        return <div className={styles.paragraph}>{decorate(children)}</div>;
      },

      h2: ({ children, ...props }) => (
        <h2 className={styles.h2} {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3 className={styles.h3} {...props}>
          {children}
        </h3>
      ),
      h4: ({ children, ...props }) => (
        <h4 className={styles.h4} {...props}>
          {children}
        </h4>
      ),
      ul: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ul className={styles.ul} {...props}>
            {children}
          </ul>
        </InListContext.Provider>
      ),
      ol: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ol className={styles.ol} {...props}>
            {children}
          </ol>
        </InListContext.Provider>
      ),
      li: ({ node: _node, children, ...props }) => (
        <li className={styles.li} {...props}>
          {decorate(children)}
        </li>
      ),
      table: ({ children, ...props }) => (
        <table className={styles.table} {...props}>
          {children}
        </table>
      ),
      th: ({ children, ...props }) => (
        <th className={styles.th} {...props}>
          {decorate(children)}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td className={styles.td} {...props}>
          {decorate(children)}
        </td>
      ),
      strong: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      b: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      em: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      i: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      code: ({ children, ...props }: any) => (
        <code className={styles.code} {...props}>
          {children}
        </code>
      ),
      sub: ({ children, ...props }: any) => (
        <sub className={styles.subSup} {...props}>
          {children}
        </sub>
      ),
      sup: ({ children, ...props }: any) => (
        <sup className={styles.subSup} {...props}>
          {children}
        </sup>
      ),
    };
  }, [styles]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <span className="as-spin" aria-hidden="true" />
          <span>Loading draft…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <Notice tone="danger" title="Couldn't load this draft">
          {error}
        </Notice>
      </div>
    );
  }

  if (draftFailed) {
    return (
      <div className={styles.root}>
        <Notice tone="danger" title="Drafting failed">
          {section.failureReason ? `${section.failureReason}. ` : ""}Use Draft this section to try again.
        </Notice>
      </div>
    );
  }

  if (section.status === "drafting") {
    const steps = activity.filter((l) => l.depth === 0 && l.kind !== "tool_done").slice(-4);
    return (
      <div className={styles.root} aria-busy="true">
        <h2 className={styles.writingTitle}>Writing this section</h2>
        <p className={styles.writingText}>
          The text appears here and at the &ldquo;{section.number} {section.title}&rdquo; heading in Word as soon as it
          is ready.
        </p>
        {steps.length > 0 ? (
          <ul className="as-act" data-testid="draft-writing-steps">
            {steps.map((line, i) => (
              <li key={line.id}>
                {i === steps.length - 1 ? (
                  <span className="as-spin" aria-hidden="true" />
                ) : (
                  <Icon name="check" className="as-check" />
                )}
                <span>{line.text}</span>
              </li>
            ))}
          </ul>
        ) : section.subline ? (
          <ul className="as-act">
            <li>
              <span className="as-spin" aria-hidden="true" />
              <span>{section.subline}</span>
            </li>
          </ul>
        ) : null}
        <div className={styles.skeleton} aria-hidden="true">
          <div className={styles.skeletonBar} />
          <div className={styles.skeletonBar} style={{ width: "92%" }} />
          <div className={styles.skeletonBar} style={{ width: "96%" }} />
          <div className={styles.skeletonBar} style={{ width: "60%" }} />
        </div>
      </div>
    );
  }

  if (!hasDraftContent) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          {section.status === "skipped"
            ? "This section was left out of the run because it does not apply."
            : "Not drafted yet. It drafts when the run reaches it, or use Draft this section."}
        </div>
      </div>
    );
  }

  const sectionsBlocks = payload?.sections ?? [];

  // Title-page modules carry no prose body — their content is a structured
  // ``title_fields`` dict (empty ``draft_content``). Render those fields the way
  // wordWriter.ts::updateTitlePageFields paints them into the live doc, instead
  // of the (empty) markdown map that leaves the panel blank. Prefer this whenever
  // the section has title fields and no real prose body.
  const titleFields = payload?.title_fields;
  const hasProseBody = sectionsBlocks.some((b) => b.draft_content?.trim());
  if (titleFields && hasTitleFields(titleFields) && !hasProseBody) {
    return (
      <div className={styles.root}>
        <div className={styles.sectionMeta}>
          {section.number} · {section.title}
        </div>
        <TitleFieldsPreview fields={titleFields} />
      </div>
    );
  }

  if (sectionsBlocks.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>No content available for this section.</div>
      </div>
    );
  }

  const gapCount = sectionsBlocks.reduce((n, b) => n + countDraftGaps(b.draft_content ?? ""), 0);

  // No number/title eyebrow here: the Inspector header already names the section.
  return (
    <div className={styles.root}>
      {sectionsBlocks.map((block, blockIdx) => {
        const sanitized = normalizeTableSpacing(
          repairPartialTables(sanitizeDraftForWord(decodeEscapedWhitespace(block.draft_content))),
        );

        return (
          <div key={`${block.section_number}-${blockIdx}`}>
            {/* singleTilde:false — GFM's default single-tilde strikethrough
                would consume `~2~` before remarkSubSup can see it. */}
            <ReactMarkdown
              remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSoftBreaks, remarkSubSup]}
              components={markdownComponents}
            >
              {sanitized}
            </ReactMarkdown>
          </div>
        );
      })}
      {gapCount > 0 && (
        <div className={`as-notice as-notice-warning as-notice-row ${styles.gapNotice}`} role="status">
          <b>{`${gapCount} ${gapCount === 1 ? "gap" : "gaps"}`}</b>
          <span>
            Add the missing details to your library, or mark {gapCount === 1 ? "it" : "them"} N/A in Issues.
          </span>
        </div>
      )}
    </div>
  );
};

export default DraftTab;
